<template>
    <Head title="Create Asset" />

    <div class="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-secondary-700">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Create New Asset</h2>
                            <p class="mt-1 text-sm text-gray-600 dark:text-secondary-400">Add a new asset to your maintenance system</p>
                        </div>
                        <InfoButton title="Creating Assets" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                The Create Asset page allows you to register physical assets into your maintenance
                                management system. Assets can be equipment, machinery, vehicles, tools, or any item
                                requiring maintenance tracking. Each asset gets a unique asset tag and can be organized
                                by category, location, and parent-child relationships for complex equipment hierarchies.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>Basic Information:</strong><br>
                                  &nbsp;&nbsp;- Asset Name: Clear, descriptive name (e.g., "Hydraulic Press #3")<br>
                                  &nbsp;&nbsp;- Category: Required classification for reporting and filtering<br>
                                  &nbsp;&nbsp;- Location: Required physical location where asset is installed<br>
                                  &nbsp;&nbsp;- Parent Asset: Link to parent if this is a component (e.g., motor belongs to pump)<br>
                                  &nbsp;&nbsp;- Status: Current operational state (Operational, Down, Under Maintenance, Retired, Disposed)<br>
                                  &nbsp;&nbsp;- Condition: Physical condition assessment (Excellent, Good, Fair, Poor)<br>
                                  &nbsp;&nbsp;- Criticality: Business impact rating (Low, Medium, High, Critical)<br>
                                • <strong>Equipment Details:</strong> Manufacturer, model, serial number, and barcode
                                  for identification<br>
                                • <strong>Financial Information:</strong> Purchase date, cost, current value, warranty
                                  expiry, and installation date<br>
                                • <strong>Meter Configuration:</strong> Set up meter tracking for usage-based maintenance
                                  (Hours, Miles, Kilometers, Cycles, or None)<br>
                                • <strong>Image Upload:</strong> Add visual reference (max 2MB) for easy identification<br>
                                • <strong>Notes:</strong> Additional context, specifications, or special considerations
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Use meaningful names</strong> - Include location or identifier in the name
                                  for clarity<br>
                                • <strong>Criticality determines priority</strong> - Critical assets get faster
                                  response for issues<br>
                                • <strong>Parent-child relationships</strong> help organize complex systems
                                  (e.g., pump → motor → bearings)<br>
                                • <strong>Meter tracking</strong> enables usage-based PM schedules (e.g., oil change every
                                  5,000 hours)<br>
                                • <strong>Keep serial numbers accurate</strong> - Essential for warranty claims and
                                  parts ordering<br>
                                • <strong>Barcode field</strong> supports physical asset tagging and scanning
                                  for quick lookups<br>
                                • <strong>Upload clear photos</strong> - Helps technicians identify the right
                                  equipment<br>
                                • After creation, you can add PM schedules, work order history, and documents
                                  on the asset details page
                            </p>
                        </InfoButton>
                    </div>
                    <Link
                        :href="route('settings.maintenance.assets.index')"
                        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Back
                    </Link>
                </div>
            </div>

            <form @submit.prevent="submit" class="px-6 py-6">
                <div class="space-y-8">
                    <!-- Basic Information -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Asset Name -->
                            <div class="md:col-span-2">
                                <label for="name" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Asset Name <span class="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    v-model="form.name"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.name }"
                                />
                                <p v-if="form.errors.name" class="mt-1 text-sm text-red-600">{{ form.errors.name }}</p>
                            </div>

                            <!-- Category -->
                            <div>
                                <label for="category_id" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Category <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="category_id"
                                    v-model="form.category_id"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.category_id }"
                                >
                                    <option value="">Select a category</option>
                                    <option v-for="category in categories" :key="category.id" :value="category.id">
                                        {{ category.path_name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.category_id" class="mt-1 text-sm text-red-600">{{ form.errors.category_id }}</p>
                            </div>

                            <!-- Location -->
                            <div>
                                <label for="location_id" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Location <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="location_id"
                                    v-model="form.location_id"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.location_id }"
                                >
                                    <option value="">Select a location</option>
                                    <option v-for="location in locations" :key="location.id" :value="location.id">
                                        {{ location.path_with_type }}
                                    </option>
                                </select>
                                <p v-if="form.errors.location_id" class="mt-1 text-sm text-red-600">{{ form.errors.location_id }}</p>
                            </div>

                            <!-- Parent Asset -->
                            <div>
                                <label for="parent_asset_id" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Parent Asset</label>
                                <select
                                    id="parent_asset_id"
                                    v-model="form.parent_asset_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.parent_asset_id }"
                                >
                                    <option value="">None (Top-level asset)</option>
                                    <option v-for="asset in parentAssets" :key="asset.id" :value="asset.id">
                                        {{ asset.asset_tag }} - {{ asset.name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.parent_asset_id" class="mt-1 text-sm text-red-600">{{ form.errors.parent_asset_id }}</p>
                            </div>

                            <!-- Status -->
                            <div>
                                <label for="status" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Status <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="status"
                                    v-model="form.status"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.status }"
                                >
                                    <option value="operational">Operational</option>
                                    <option value="down">Down</option>
                                    <option value="maintenance">Under Maintenance</option>
                                    <option value="retired">Retired</option>
                                    <option value="disposed">Disposed</option>
                                </select>
                                <p v-if="form.errors.status" class="mt-1 text-sm text-red-600">{{ form.errors.status }}</p>
                            </div>

                            <!-- Condition -->
                            <div>
                                <label for="condition" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Condition</label>
                                <select
                                    id="condition"
                                    v-model="form.condition"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.condition }"
                                >
                                    <option value="">Not specified</option>
                                    <option value="excellent">Excellent</option>
                                    <option value="good">Good</option>
                                    <option value="fair">Fair</option>
                                    <option value="poor">Poor</option>
                                </select>
                                <p v-if="form.errors.condition" class="mt-1 text-sm text-red-600">{{ form.errors.condition }}</p>
                            </div>

                            <!-- Criticality -->
                            <div>
                                <label for="criticality" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Criticality <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="criticality"
                                    v-model="form.criticality"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.criticality }"
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="critical">Critical</option>
                                </select>
                                <p v-if="form.errors.criticality" class="mt-1 text-sm text-red-600">{{ form.errors.criticality }}</p>
                            </div>

                            <!-- Image Upload -->
                            <div class="md:col-span-2">
                                <label for="image" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Asset Image</label>
                                <input
                                    type="file"
                                    id="image"
                                    @change="handleImageUpload"
                                    accept="image/*"
                                    class="mt-1 block w-full text-sm text-gray-500
                                        file:mr-4 file:py-2 file:px-4
                                        file:rounded-md file:border-0
                                        file:text-sm file:font-semibold
                                        file:bg-blue-50 file:text-blue-700
                                        hover:file:bg-blue-100"
                                />
                                <p v-if="form.errors.image" class="mt-1 text-sm text-red-600">{{ form.errors.image }}</p>
                                <p class="mt-1 text-xs text-gray-500 dark:text-secondary-400">Maximum file size: 2MB</p>
                            </div>
                        </div>
                    </div>

                    <!-- Equipment Details -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Equipment Details</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Manufacturer -->
                            <div>
                                <label for="manufacturer" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Manufacturer</label>
                                <input
                                    type="text"
                                    id="manufacturer"
                                    v-model="form.manufacturer"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.manufacturer }"
                                />
                                <p v-if="form.errors.manufacturer" class="mt-1 text-sm text-red-600">{{ form.errors.manufacturer }}</p>
                            </div>

                            <!-- Model -->
                            <div>
                                <label for="model" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Model</label>
                                <input
                                    type="text"
                                    id="model"
                                    v-model="form.model"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.model }"
                                />
                                <p v-if="form.errors.model" class="mt-1 text-sm text-red-600">{{ form.errors.model }}</p>
                            </div>

                            <!-- Serial Number -->
                            <div>
                                <label for="serial_number" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Serial Number</label>
                                <input
                                    type="text"
                                    id="serial_number"
                                    v-model="form.serial_number"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.serial_number }"
                                />
                                <p v-if="form.errors.serial_number" class="mt-1 text-sm text-red-600">{{ form.errors.serial_number }}</p>
                            </div>

                            <!-- Barcode -->
                            <div>
                                <label for="barcode" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Barcode</label>
                                <input
                                    type="text"
                                    id="barcode"
                                    v-model="form.barcode"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.barcode }"
                                />
                                <p v-if="form.errors.barcode" class="mt-1 text-sm text-red-600">{{ form.errors.barcode }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Financial Information -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Financial Information</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Purchase Date -->
                            <div>
                                <label for="purchase_date" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Purchase Date</label>
                                <input
                                    type="date"
                                    id="purchase_date"
                                    v-model="form.purchase_date"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.purchase_date }"
                                />
                                <p v-if="form.errors.purchase_date" class="mt-1 text-sm text-red-600">{{ form.errors.purchase_date }}</p>
                            </div>

                            <!-- Installation Date -->
                            <div>
                                <label for="installation_date" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Installation Date</label>
                                <input
                                    type="date"
                                    id="installation_date"
                                    v-model="form.installation_date"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.installation_date }"
                                />
                                <p v-if="form.errors.installation_date" class="mt-1 text-sm text-red-600">{{ form.errors.installation_date }}</p>
                            </div>

                            <!-- Purchase Cost -->
                            <div>
                                <label for="purchase_cost" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Purchase Cost</label>
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span class="text-gray-500 dark:text-secondary-400 sm:text-sm">{{ currencySymbol }}</span>
                                    </div>
                                    <input
                                        type="number"
                                        id="purchase_cost"
                                        v-model="form.purchase_cost"
                                        step="0.01"
                                        min="0"
                                        class="block w-full pl-7 rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        :class="{ 'border-red-300': form.errors.purchase_cost }"
                                    />
                                </div>
                                <p v-if="form.errors.purchase_cost" class="mt-1 text-sm text-red-600">{{ form.errors.purchase_cost }}</p>
                            </div>

                            <!-- Current Value -->
                            <div>
                                <label for="current_value" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Current Value</label>
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span class="text-gray-500 dark:text-secondary-400 sm:text-sm">{{ currencySymbol }}</span>
                                    </div>
                                    <input
                                        type="number"
                                        id="current_value"
                                        v-model="form.current_value"
                                        step="0.01"
                                        min="0"
                                        class="block w-full pl-7 rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        :class="{ 'border-red-300': form.errors.current_value }"
                                    />
                                </div>
                                <p v-if="form.errors.current_value" class="mt-1 text-sm text-red-600">{{ form.errors.current_value }}</p>
                            </div>

                            <!-- Warranty Expiry -->
                            <div>
                                <label for="warranty_expiry_date" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Warranty Expiry Date</label>
                                <input
                                    type="date"
                                    id="warranty_expiry_date"
                                    v-model="form.warranty_expiry_date"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.warranty_expiry_date }"
                                />
                                <p v-if="form.errors.warranty_expiry_date" class="mt-1 text-sm text-red-600">{{ form.errors.warranty_expiry_date }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Meter Configuration -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Meter Configuration</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Meter Type -->
                            <div>
                                <label for="meter_type" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Meter Type <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="meter_type"
                                    v-model="form.meter_type"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.meter_type }"
                                >
                                    <option value="none">None</option>
                                    <option value="hours">Hours</option>
                                    <option value="miles">Miles</option>
                                    <option value="kilometers">Kilometers</option>
                                    <option value="cycles">Cycles</option>
                                </select>
                                <p v-if="form.errors.meter_type" class="mt-1 text-sm text-red-600">{{ form.errors.meter_type }}</p>
                            </div>

                            <!-- Current Meter Reading -->
                            <div v-if="form.meter_type !== 'none'">
                                <label for="current_meter_reading" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Current Meter Reading
                                </label>
                                <input
                                    type="number"
                                    id="current_meter_reading"
                                    v-model="form.current_meter_reading"
                                    min="0"
                                    step="0.01"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.current_meter_reading }"
                                />
                                <p v-if="form.errors.current_meter_reading" class="mt-1 text-sm text-red-600">
                                    {{ form.errors.current_meter_reading }}
                                </p>
                            </div>

                            <!-- Meter Unit -->
                            <div v-if="form.meter_type !== 'none'">
                                <label for="meter_unit" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Meter Unit</label>
                                <input
                                    type="text"
                                    id="meter_unit"
                                    v-model="form.meter_unit"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.meter_unit }"
                                    :placeholder="getMeterUnitPlaceholder()"
                                />
                                <p v-if="form.errors.meter_unit" class="mt-1 text-sm text-red-600">{{ form.errors.meter_unit }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Notes -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Additional Information</h3>
                        <div>
                            <label for="notes" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Notes</label>
                            <textarea
                                id="notes"
                                v-model="form.notes"
                                rows="4"
                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                :class="{ 'border-red-300': form.errors.notes }"
                                placeholder="Add any additional notes about this asset..."
                            ></textarea>
                            <p v-if="form.errors.notes" class="mt-1 text-sm text-red-600">{{ form.errors.notes }}</p>
                        </div>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="flex items-center justify-end space-x-3 pt-6 mt-6 border-t border-gray-200 dark:border-secondary-700">
                    <Link
                        :href="route('settings.maintenance.assets.index')"
                        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        :disabled="form.processing"
                        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span v-if="form.processing">Creating...</span>
                        <span v-else>Create Asset</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';
import { useCurrency } from '@/Composables/useCurrency';

const props = defineProps({
    categories: Array,
    locations: Array,
    parentAssets: Array,
});

const toast = useToast();
const { currencySymbol } = useCurrency();

const form = useSecureForm({
    name: '',
    category_id: '',
    parent_asset_id: '',
    location_id: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    barcode: '',
    status: 'operational',
    condition: '',
    criticality: 'medium',
    purchase_date: '',
    purchase_cost: '',
    current_value: '',
    warranty_expiry_date: '',
    installation_date: '',
    meter_type: 'none',
    current_meter_reading: '',
    meter_unit: '',
    specifications: {},
    notes: '',
    image: null,
});

const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
        form.image = file;
    }
};

const getMeterUnitPlaceholder = () => {
    const placeholders = {
        hours: 'e.g., operating hours',
        miles: 'e.g., miles driven',
        kilometers: 'e.g., kilometers driven',
        cycles: 'e.g., operation cycles',
        none: '',
    };
    return placeholders[form.meter_type] || '';
};

const submit = () => {
    form.post(route('settings.maintenance.assets.store'), {
        onError: () => {
            toast.error('Failed to create asset. Please check the form for errors.');
        },
    });
};
</script>
